import React, { FC, useEffect, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate } from 'react-router-dom';

import PDF from './pages/events/PDF';
import SignIn from './components/auth/SignIn';
import SettingsPage from './pages/SettingsPage';
import EventUsers from './pages/events/EventUsers';
import NotFoundPage from './pages/common/NotFoundPage';
import PrepModePage from './pages/events/PrepModePage';
import AuthRequired from 'components/auth/AuthRequired';
import EventTimeline from './pages/events/EventTimeline';
import JoinEventPage from './pages/events/JoinEventPage';
import ForbiddenPage from './pages/common/ForbiddenPage';
import EventsHistory from './pages/events/EventsHistory';
import EventNotifPage from 'pages/events/EventNotifPage';
import AdminRequired from 'components/auth/AdminRequired';
import EventsUpcoming from './pages/events/EventsUpcoming';
import AuthNotRequired from 'components/auth/AuthNotRequired';
import AuthRequiredInline from 'components/auth/AuthRequiredInline';
import SharedTemplatePage from './pages/template/SharedTemplatePage';
import SharedPlaylistPage from './pages/songIdeas/SharedPlaylistPage';

import Me from './pages/users/Me';
import ScannerPage from './pages/ScannerPage';
import AdminUsers from './pages/admin/Users';
import Profile from './components/user/Profile';
import EventLayout from 'pages/events/EventLayout';
import Templates from './pages/template/Templates';
import SongIdeas from './pages/songIdeas/SongIdeas';
import AuthSpotify from './components/auth/AuthSpotify';
import ChildDjProfile from './pages/users/ChildDjProfile';
import TemplateLayout from 'pages/template/TemplateLayout';
import ViboWelcome from 'components/splashScreens/ViboWelcome';
import TemplateInvite from 'components/template/TemplateInvite';
import TemplateNotifPage from 'pages/template/TemplateNotifPage';
import AppleMusicConnect from './pages/common/AppleMusicConnect';
import SongIdeasDetails from './pages/songIdeas/SongIdeasDetails';
import TemplateTimeline from 'components/template/TemplateTimeline';
import { GA4ContextProvider } from './components/context/GA4Context';
import { ModalWindowContextProvider } from 'vibo-ui/Modal/ModalWindowContext';

import moment from './services/moment';

import useCommonStyles from './resources/styles/global/app/style';

const BeatsourcePage: FC = lazy(() => import('./pages/BeatsourcePage'));
const CheckoutPage: FC = lazy(() => import('./pages/CheckoutPage'));
const MarketingAssetsPage: FC = lazy(() => import('./pages/MarketingAssetsPage'));
const AcademyPage: FC = lazy(() => import('./pages/AcademyPage'));
const LessonDetailsPage: FC = lazy(() => import('./pages/LessonDetailsPage'));
const CheckoutFailPage: FC = lazy(() => import('./pages/CheckoutFailPage'));
const CheckoutSuccessPage: FC = lazy(() => import('./pages/CheckoutSuccessPage'));
const HealthCheckPage: FC = lazy(() => import('./pages/admin/HealthCheckPage'));
const AuthBeatsourcePage: FC = lazy(() => import('./pages/AuthBeatsourcePage'));
const FavoriteSectionsPage: FC = lazy(() => import('./pages/events/FavoriteSectionsPage'));
const WebHookPage: FC = lazy(() => import('./pages/admin/WebHookPage'));
const ForgotPassword: FC = lazy(() => import('./components/auth/ForgotPassword'));
const ForgotPasswordSuccess: FC = lazy(() =>
  import('./components/auth/ForgotPassword/ForgotPasswordSuccess')
);
const RestorePassword: FC = lazy(() => import('./components/auth/RestorePassword'));
const ResetPasswordSuccess: FC = lazy(() =>
  import('./components/auth/RestorePassword/RestorePasswordSuccess')
);

const AppRoutes: FC = () => {
  const { i18n } = useTranslation();

  useCommonStyles();

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  return (
    <GA4ContextProvider>
      <Routes>
        <Route element={<ModalWindowContextProvider />}>
          <Route element={<AuthRequired />}>
            <Route path="/" element={<Navigate to="/events/upcoming" />} />
            <Route path="/events/upcoming" element={<EventsUpcoming />} />
            <Route path="/events/history" element={<EventsHistory />} />
            <Route path="/event/:id" element={<EventLayout />}>
              <Route path="timeline/:sectionId?" element={<EventTimeline />} />
              <Route path="notifications" element={<EventNotifPage />} />
              <Route path="users" element={<EventUsers />} />
              <Route path="prepMode/:sectionId?" element={<PrepModePage />} />
              <Route path="beatsource/:sectionId?" element={<BeatsourcePage />} />
              <Route path="pdf" element={<PDF />} />
            </Route>
            <Route path="/template/:id" element={<TemplateLayout />}>
              <Route path="users" element={<TemplateInvite />} />
              <Route path="timeline/:sectionId?" element={<TemplateTimeline />} />
              <Route path="notifications" element={<TemplateNotifPage />} />
            </Route>
            <Route path="/favorite-sections/:sectionId?" element={<FavoriteSectionsPage />} />
            <Route path="/songIdeas/v/:id" element={<SongIdeasDetails />} />
            <Route path="/songIdeas/:type" element={<SongIdeas />} />
            <Route path="/templates/:type?" element={<Templates />} />
            <Route path="/scanner" element={<ScannerPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/account" element={<Profile />} />
            <Route path="/me" element={<Me />} />
            <Route path="/profile/:id" element={<ChildDjProfile />} />
            <Route path="/marketing-assets" element={<MarketingAssetsPage />} />
            <Route path="/academy" element={<AcademyPage />} />
            <Route path="/lesson/:courseId/:id" element={<LessonDetailsPage />} />
            <Route path="/redirect-beatsource-auth-result" element={<AuthBeatsourcePage />} />
            <Route path="/vibo-welcome" element={<ViboWelcome />} />
          </Route>
          <Route element={<AdminRequired />}>
            <Route path="/admin" element={<AdminUsers />} />
            <Route path="/health-check" element={<HealthCheckPage />} />
            <Route path="/web-hook-test" element={<WebHookPage />} />
          </Route>
          <Route element={<AuthRequiredInline />}>
            <Route path="/spotify-connect" element={<AuthSpotify />} />
            <Route path="/checkout" element={<CheckoutPage />} />
          </Route>
          <Route element={<AuthNotRequired />}>
            <Route path="/login" element={<SignIn />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/forgot-password-success" element={<ForgotPasswordSuccess />} />
            <Route path="/reset/:token" element={<RestorePassword />} />
            <Route path="/reset-password-success" element={<ResetPasswordSuccess />} />
          </Route>
          <Route path="/join-event" element={<JoinEventPage />} />
          <Route path="/share-template" element={<SharedTemplatePage />} />
          <Route path="/share-playlist" element={<SharedPlaylistPage />} />
          <Route path="/connect-applemusic" element={<AppleMusicConnect />} />
          <Route path="/checkout-success" element={<CheckoutSuccessPage />} />
          <Route path="/checkout-fail" element={<CheckoutFailPage />} />
          <Route path="/forbidden" element={<ForbiddenPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </GA4ContextProvider>
  );
};

export default AppRoutes;
